import { falBagShoppingPlus } from 'shared/@generated/icons/falBagShoppingPlus';
import { falMinus } from 'shared/@generated/icons/falMinus';
import { falPlus } from 'shared/@generated/icons/falPlus';
import { useTranslation } from 'react-i18next';
import { Order, Product, ProductVariant } from 'shared/@generated/models';
import { Button } from 'shared/components/button';
import { Icon } from 'shared/components/icon';
import { TouchableOpacity, View } from 'react-native';
import { Text } from 'shared/components/text';
import useApi from 'shared/library/use-api';
import { apiRoute } from 'shared/library/api-route';
import useShopStore from 'shared/state/shop';
import { useLocalStorage } from 'shared/library/use-local-storage';
import { notify } from 'shared/library/notify';
import { analytics } from 'shared/library/analytics';

interface AddToCartButtonProps {
  product: Product;
  variant: ProductVariant;
  size?: 'default' | 'compact';
}

function AddToCartButton({ product, variant, size = 'default' }: AddToCartButtonProps) {
  const { t } = useTranslation('screens');
  const api = useApi();
  const { setStoredValue } = useLocalStorage('order_uuid');
  const order = useShopStore((state) => state.order);
  const setOrder = useShopStore((state) => state.setOrder);
  const compact = size === 'compact';
  const quantity =
    order?.order_items.find((item) => item.product_variant_id === variant.id)?.quantity ?? 0;

  const createOrder = async (): Promise<Order> => {
    const response = await api.request<{ order: Order }>(apiRoute('orders.store'));

    setOrder(response.order);
    await setStoredValue(response.order.uuid);

    return response.order;
  };

  const updateItemQuantity = async (nextQuantity: number) => {
    const activeOrder = order ?? (await createOrder());
    const quantityDiff = nextQuantity - quantity;

    const response = await api.request<{ order: Order }>(
      apiRoute('orders.update-item-quantity', { order: activeOrder.uuid }),
      {
        product_variant_id: variant.id,
        quantity: nextQuantity,
      },
    );

    if (nextQuantity === 1 && quantity === 0) {
      notify.success(t('shop.shop_product.added_to_cart'));
    } else if (nextQuantity === 0 && quantity > 0) {
      notify.success(t('shop.shop_product.removed_from_cart'));
    }

    if (quantityDiff > 0) {
      analytics.event('add_to_cart', {
        currency: variant.pricing.local.total.currency,
        value: variant.pricing.local.total.amount * quantityDiff,
        item_id: variant.uuid,
        item_name: product.name,
        item_variant: variant.name,
        price: variant.pricing.local.total.amount,
        quantity: quantityDiff,
      });
    } else if (quantityDiff < 0) {
      analytics.event('remove_from_cart', {
        currency: variant.pricing.local.total.currency,
        value: variant.pricing.local.total.amount * Math.abs(quantityDiff),
        item_id: variant.uuid,
        item_name: product.name,
        item_variant: variant.name,
        price: variant.pricing.local.total.amount,
        quantity: Math.abs(quantityDiff),
      });
    }

    setOrder(response.order);
  };

  if (quantity > 0) {
    return (
      <View
        className={
          compact
            ? 'flex-row items-center gap-1.5 self-start rounded bg-stone-200/50 p-1'
            : 'bg-brand-cream-dark flex-row items-center justify-between rounded p-2'
        }
      >
        <TouchableOpacity
          className={
            compact
              ? 'size-6 items-center justify-center rounded-lg bg-white'
              : 'size-10 items-center justify-center rounded-xl bg-white'
          }
          onPress={() => updateItemQuantity(Math.max(quantity - 1, 0))}
          disabled={api.loading || !variant.available}
        >
          <Icon
            icon={falMinus}
            className={compact ? 'text-brand-brown-dark size-3' : 'text-brand-brown-dark'}
          />
        </TouchableOpacity>

        <Text
          className={
            compact
              ? 'text-brand-brown-dark min-w-8 px-1 text-center font-black'
              : 'text-brand-brown-dark text-2xl font-black'
          }
        >
          {quantity}
        </Text>

        <TouchableOpacity
          className={
            compact
              ? 'size-6 items-center justify-center rounded-lg bg-white'
              : 'size-10 items-center justify-center rounded-xl bg-white'
          }
          onPress={() => updateItemQuantity(quantity + 1)}
          disabled={api.loading || !variant.available}
        >
          <Icon
            icon={falPlus}
            className={compact ? 'text-brand-brown-dark size-3' : 'text-brand-brown-dark'}
          />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <Button
      text={t('shop.shop_product.add_to_cart')}
      icon={falBagShoppingPlus}
      iconPosition={'left'}
      center={true}
      dense={compact}
      className={compact ? 'self-start' : undefined}
      onPress={() => updateItemQuantity(1)}
      loading={api.loading}
    />
  );
}

export { AddToCartButton };
